var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { action, runInAction } from "mobx";
import { Observer } from "mobx-react-lite";
import React, { useEffect, useRef } from "react";
import { addToArrayIfNew } from "../../utils/array.utils";
import joinClassName from "../../utils/className.utils";
import { checkIfShouldInvertStyle } from "../../utils/colors.utils";
import { useProps, useStore } from "../../utils/mobx.utils";
import { capitalizeFirstLetter } from "../../utils/string.utils";
import BaseLabel from "../BaseLabel/BaseLabel";
import './BaseSelector.scss';
import BaseSelectorInlineInner from "./BaseSelectorInlineInner";
import BaseSelectorSystemInner from "./BaseSelectorSystemInner";
var BaseSelector = function (props) {
    var p = useProps(props);
    var ref = useRef(null);
    var s = useStore(function () {
        var defaultOptionEqualityChecker = function (a, b) { return a === b; };
        var defaultValueEqualityChecker = function (a, b) { return a === b; };
        var defaultGetter = function (a) { return typeof a === 'string' ? a : a.value; };
        var defaultLabelRenderer = function (a) { return a instanceof Object ? (a.label || a.name) : a; };
        var defaultDisabledOptionChecker = function (a) { return a instanceof Object ? a.disabled : false; };
        var defaultOnChange = function (v) { };
        var defaultOnBlur = function (v) { };
        function isMultipleMode(value) {
            return value instanceof Array;
        }
        var store = {
            shouldInvertStyle: false,
            get valueFieldRef() {
                if (!p.form) {
                    console.error('BaseSelector was not provided with a valid form. Props:', props);
                }
                var value = p.form[p.field];
                return (value === null ? '' : value);
            },
            set valueFieldRef(newValue) {
                if (newValue === undefined) {
                    // @ts-ignore
                    p.form[p.field] = store.emptyValue;
                }
                // @ts-ignore
                p.form[p.field] = newValue;
            },
            get isMultiple() {
                return isMultipleMode(store.valueFieldRef);
            },
            get appearance() {
                return p.appearance || 'inline';
            },
            get valueGetter() {
                return p.valueGetter || defaultGetter;
            },
            get optionLabelRenderer() {
                return p.optionLabelRenderer || defaultLabelRenderer;
            },
            get optionEqualityChecker() {
                return p.optionEqualityChecker || defaultOptionEqualityChecker;
            },
            get valueEqualityChecker() {
                return p.valueEqualityChecker || defaultValueEqualityChecker;
            },
            get disabledOptionChecker() {
                return p.disabledOptionChecker || defaultDisabledOptionChecker;
            },
            get optional() {
                var _a;
                return (_a = p.optional) !== null && _a !== void 0 ? _a : true;
            },
            get required() {
                var _a;
                return (_a = p.required) !== null && _a !== void 0 ? _a : false;
            },
            get disabled() {
                var _a;
                return (_a = p.disabled) !== null && _a !== void 0 ? _a : false;
            },
            get nullable() {
                var _a;
                return (_a = p.nullable) !== null && _a !== void 0 ? _a : true;
            },
            get inverted() {
                return checkIfShouldInvertStyle(ref);
            },
            get defaultValue() {
                var _a, _b;
                if (store.valueFieldRef instanceof Array) {
                    return ((_a = p.defaultValue) !== null && _a !== void 0 ? _a : []);
                }
                else {
                    return ((_b = p.defaultValue) !== null && _b !== void 0 ? _b : '');
                }
            },
            get emptyValue() {
                var _a;
                return ((_a = p.emptyValue) !== null && _a !== void 0 ? _a : '');
            },
            get onChange() {
                return p.onChange || defaultOnChange;
            },
            get onBlur() {
                return p.onBlur || defaultOnBlur;
            },
            handleChange: function (suppliedValue) {
                var hasChanged = store.select(suppliedValue);
                hasChanged && p.onChange && p.onChange(store.valueFieldRef);
            },
            handleBlur: function (suppliedValue) {
                // console.log(suppliedValue);
                p.onBlur && p.onBlur(store.valueFieldRef);
            },
            select: action(function (suppliedValue) {
                // console.log(suppliedValue, isMultipleMode(store.valueFieldRef));
                if (p.readonly)
                    return;
                var toReturn;
                switch (true) {
                    case (suppliedValue === undefined): {
                        toReturn = s.clearSelection();
                        break;
                    }
                    case (isMultipleMode(store.valueFieldRef)): {
                        toReturn = s.selectMultiple(suppliedValue);
                        break;
                    }
                    default: {
                        toReturn = s.selectSingle(suppliedValue);
                        break;
                    }
                }
                // console.log(s.valueFieldRef);
                // if (s.appearance === 'checkboxGroup') {
                //   p.onChange?.(s.valueFieldRef);
                // }
                return toReturn;
            }),
            clearSelection: action(function () {
                var toReturn = true;
                if (isMultipleMode(store.valueFieldRef)) {
                    toReturn = false;
                }
                else {
                    if (p.nullable) {
                        // @ts-ignore
                        store.valueFieldRef = store.emptyValue;
                        toReturn = true;
                    }
                    else {
                        toReturn = false;
                    }
                }
                // if (s.appearance === 'checkboxGroup') {
                //   p.onChange?.(s.valueFieldRef);
                // }
                return toReturn;
            }),
            selectMultiple: action(function (suppliedValue) {
                if (!(store.valueFieldRef instanceof Array))
                    return;
                if (suppliedValue instanceof Array) {
                    store.valueFieldRef.splice(0);
                    addToArrayIfNew.apply(void 0, __spreadArray([store.valueFieldRef], suppliedValue, false));
                    return true;
                }
                else {
                    var i = store.valueFieldRef.findIndex(function (a) { return store.valueEqualityChecker(a, suppliedValue); });
                    // console.log('i', i);
                    // console.log('nullable', p.nullable);
                    // console.log('supplied', suppliedValue);
                    // console.log('current value', store.valueFieldRef);
                    if (i >= 0) {
                        if (!p.nullable && store.valueFieldRef.length === 1) {
                            // console.log('not nullable, must not remove');
                            return false;
                        }
                        // console.log('nullable, remove');
                        store.valueFieldRef.splice(i, 1);
                    }
                    else {
                        addToArrayIfNew(store.valueFieldRef, '' + suppliedValue);
                    }
                    return true;
                }
            }),
            selectSingle: action(function (suppliedValue) {
                // console.log(store.valueFieldRef, suppliedValue);
                if (s.valueEqualityChecker(store.valueFieldRef, suppliedValue)) {
                    if (p.nullable) {
                        // @ts-ignore
                        store.valueFieldRef = store.emptyValue;
                        return true;
                    }
                    else {
                        return false;
                    }
                }
                else {
                    // @ts-ignore
                    store.valueFieldRef = suppliedValue;
                }
                return true;
            }),
            isSelectedOption: function (o) {
                var value = s.valueGetter(o);
                return s.isSelectedValue(value);
            },
            isSelectedValue: function (value) {
                if (value === undefined)
                    return false;
                if (isMultipleMode(s.valueFieldRef)) {
                    var i = s.valueFieldRef.findIndex(function (a) { return store.valueEqualityChecker(a, value); });
                    // console.log(s.valueFieldRef, value, i);
                    return i >= 0;
                }
                else {
                    return store.valueEqualityChecker(store.valueFieldRef, value);
                }
            },
        };
        return store;
    });
    var renderInner = function () {
        var _a;
        var valueFieldRef = s.valueFieldRef, valueGetter = s.valueGetter, optionLabelRenderer = s.optionLabelRenderer, valueEqualityChecker = s.valueEqualityChecker, disabledOptionChecker = s.disabledOptionChecker, required = s.required, disabled = s.disabled, emptyValue = s.emptyValue, handleChange = s.handleChange, handleBlur = s.handleBlur, isMultiple = s.isMultiple, isSelectedOption = s.isSelectedOption;
        var relayedProps = {
            name: (_a = p.name) !== null && _a !== void 0 ? _a : p.field,
            options: p.options,
            valueFieldRef: valueFieldRef,
            valueGetter: valueGetter,
            optionLabelRenderer: optionLabelRenderer,
            valueEqualityChecker: valueEqualityChecker,
            disabledOptionChecker: disabledOptionChecker,
            required: required,
            disabled: disabled,
            emptyValue: emptyValue,
            colorCodedState: p.colorCodedState,
            onChange: handleChange,
            onBlur: handleBlur,
            multiple: isMultiple,
            isSelectedOption: isSelectedOption,
            placeholder: p.placeholder,
            iconVariant: p.iconVariant
        };
        switch (s.appearance) {
            case 'system': return React.createElement(BaseSelectorSystemInner, __assign({}, relayedProps));
            case 'inline': return React.createElement(BaseSelectorInlineInner, __assign({}, relayedProps));
            default: return React.createElement(BaseSelectorSystemInner, __assign({}, relayedProps));
        }
    };
    useEffect(function () {
        runInAction(function () {
            var shouldInvertStyle = checkIfShouldInvertStyle(ref);
            if (shouldInvertStyle !== s.shouldInvertStyle)
                s.shouldInvertStyle = shouldInvertStyle;
        });
    });
    return React.createElement(Observer, { children: function () {
            var className = p.className, label = p.label, optional = p.optional, required = p.required, disabled = p.disabled, colorCodedState = p.colorCodedState, appearance = p.appearance, _a = p.checkboxGroupDirection, checkboxGroupDirection = _a === void 0 ? 'row' : _a, readonly = p.readonly;
            var c = joinClassName('BaseSelector', "BaseSelector" + capitalizeFirstLetter(s.appearance), className, colorCodedState && "state-" + colorCodedState, disabled && 'disabled', readonly && 'readonly', s.shouldInvertStyle && 'inverted');
            var labelProps = { optional: optional, required: required };
            return React.createElement("div", { className: c, ref: ref, "data-mode": s.isMultiple ? 'multiple' : 'single', "data-value": s.valueFieldRef, "data-cy": p.dataCy },
                label && React.createElement(BaseLabel, __assign({}, labelProps), label),
                renderInner(),
                p.infoAfterInputField && React.createElement("div", { className: "BaseSelectInfoAfterInputField" }, p.infoAfterInputField));
        } });
};
export default BaseSelector;
